import { type ChangeEvent } from 'react';
import { motion } from 'framer-motion';
import type { AssumptionGroup, AssumptionField, Assumptions } from '@/types/models';
import { Input } from '@/components/ui/Input';

interface AssumptionsFormProps {
  groups: AssumptionGroup[];
  values: Assumptions | null;
  onChange: (values: Assumptions) => void;
  isLoading?: boolean;
}

interface AssumptionFieldInputProps {
  field: AssumptionField;
  value: string | number | boolean | undefined;
  onChange: (fieldId: string, value: string | number | boolean) => void;
}

/**
 * Single assumption field input
 * Renders the appropriate input type based on field configuration
 */
function AssumptionFieldInput({ field, value, onChange }: AssumptionFieldInputProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const newValue = field.type === 'number' 
      ? parseFloat(e.target.value) || 0
      : e.target.value;
    onChange(field.id, newValue);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(field.id, e.target.checked);
  };

  // Render checkbox
  if (field.type === 'checkbox') {
    return (
      <div className="input-group flex items-center gap-3">
        <input
          type="checkbox"
          id={field.id}
          checked={value !== undefined ? Boolean(value) : Boolean(field.defaultValue)}
          onChange={handleCheckboxChange}
          className="w-4 h-4 text-primary-600 bg-white border-slate-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
        />
        <div className="flex flex-col">
          <label
            htmlFor={field.id}
            className="font-medium text-slate-700 text-sm cursor-pointer"
          >
            {field.label}
          </label>
          {field.helpText && (
            <p className="text-xs text-slate-500">{field.helpText}</p>
          )}
        </div>
      </div>
    );
  }

  // Helper to get non-boolean value for input elements
  const getNonBooleanValue = (v: typeof value, defaultVal: typeof field.defaultValue): string | number => {
    if (typeof v === 'boolean') return '';
    if (typeof defaultVal === 'boolean') return v ?? '';
    return v ?? defaultVal ?? '';
  };

  // Render select dropdown
  if (field.type === 'select' && field.options) {
    const selectValue = getNonBooleanValue(value, field.defaultValue);
    return (
      <div className="input-group">
        <label
          htmlFor={field.id}
          className="block mb-1.5 font-medium text-slate-700 text-sm"
        >
          {field.label}
        </label>
        <select
          id={field.id}
          value={selectValue}
          onChange={handleChange}
          className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
        >
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {field.helpText && (
          <p className="mt-1 text-xs text-slate-500">{field.helpText}</p>
        )}
      </div>
    );
  }

  // Render textarea
  if (field.type === 'textarea') {
    const textareaValue = getNonBooleanValue(value, field.defaultValue);
    return (
      <div className="input-group">
        <label
          htmlFor={field.id}
          className="block mb-1.5 font-medium text-slate-700 text-sm"
        >
          {field.label}
        </label>
        <textarea
          id={field.id}
          value={textareaValue}
          onChange={handleChange}
          placeholder={field.placeholder}
          className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 min-h-[80px] resize-y"
        />
        {field.helpText && (
          <p className="mt-1 text-xs text-slate-500">{field.helpText}</p>
        )}
      </div>
    );
  }

  // Default: render standard input (number, email, text)
  const inputValue = getNonBooleanValue(value, field.defaultValue);
  return (
    <Input
      id={field.id}
      label={field.label}
      type={field.type}
      value={inputValue}
      onChange={handleChange}
      placeholder={field.placeholder}
      min={field.min}
      max={field.max}
      step={field.step ?? (field.type === 'number' ? 'any' : undefined)}
      required={field.required}
      helpText={field.helpText}
    />
  );
}

/**
 * Single assumption group (card with multiple fields)
 */
function AssumptionGroupCard({
  group,
  values,
  onChange,
  index,
}: {
  group: AssumptionGroup;
  values: Assumptions | null;
  onChange: (fieldId: string, value: string | number | boolean) => void;
  index: number;
}) {
  return (
    <motion.div
      className="assumption-group bg-slate-50 p-6 rounded-lg border border-slate-200 border-l-4 border-l-primary-500"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <h3 className="text-lg font-semibold text-slate-800 mb-4">
        {group.title}
      </h3>
      <div className="space-y-4">
        {group.fields.map((field) => (
          <AssumptionFieldInput
            key={field.id}
            field={field}
            value={values?.[field.id] as string | number | boolean | undefined}
            onChange={onChange}
          />
        ))}
      </div>
    </motion.div>
  );
}

/**
 * Main AssumptionsForm component
 * Renders the complete assumptions form from configuration
 */
export function AssumptionsForm({
  groups,
  values,
  onChange,
  isLoading = false,
}: AssumptionsFormProps) {
  const handleFieldChange = (fieldId: string, value: string | number | boolean) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  if (isLoading) {
    return (
      <div className="assumptions-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="bg-slate-100 animate-pulse rounded-lg h-64"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="assumptions-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {groups.map((group, index) => (
        <AssumptionGroupCard
          key={group.id}
          group={group}
          values={values}
          onChange={handleFieldChange}
          index={index}
        />
      ))}
    </div>
  );
}
